import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from '@remix-run/react';
import styles from './tailwind.css?url';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import bgImage from './assets/background-pattern.jpg';
import { useEffect } from 'react';
import { authenticator } from './services/auth.server';
import UserAccount from './models/UserAccount.server';
import { getSession, commitSession } from './services/session.server';
import { redirect } from '@remix-run/node';

export const meta = () => {
  return [
    { title: 'DocsAutomator' },
    {
      name: 'description',
      content: 'Professional document automation using Google Docs.',
    },
  ];
};

export const links = () => [{ rel: 'stylesheet', href: styles }];

export async function loader({ request }) {
  // Check if user is authenticated
  const sessionUser = await authenticator.isAuthenticated(request);

  if (sessionUser) {
    // Get user account with workspaces
    const userAccount = await UserAccount.findById(sessionUser._id).lean();

    // if user is not found, logout and redirect to login, thereby deleting the session
    if (!userAccount) {
      return await authenticator.logout(request, { redirectTo: '/login' });
    }

    // Check if activeWorkspace on sessionUser is still valid (exists in workspaces array)
    const activeWorkspaceIsValid = userAccount.workspaces?.some(
      (w) => w.workspace.toString() === sessionUser.activeWorkspace?.toString()
    );

    if (!activeWorkspaceIsValid || !sessionUser.activeWorkspace) {
      // Check if user has other workspaces
      if (userAccount.workspaces && userAccount.workspaces.length > 0) {
        // Set the first available workspace as active
        const newActiveWorkspace = userAccount.workspaces[0].workspace;
        await UserAccount.findByIdAndUpdate(userAccount._id, {
          activeWorkspace: newActiveWorkspace,
        });

        // Update session with new active workspace as well as workspaces array from userAccount
        const session = await getSession(request.headers.get('cookie'));
        const updatedUser = {
          ...sessionUser,
          activeWorkspace: newActiveWorkspace,
          workspaces: userAccount.workspaces,
        };
        session.set('user', updatedUser);

        // Redirect to refresh the page with new active workspace
        return redirect('/', {
          headers: {
            'Set-Cookie': await commitSession(session),
          },
        });
      } else {
        // Check if we're not already on the create-workspace page to avoid infinite loop
        const url = new URL(request.url);
        if (url.pathname !== '/create-workspace') {
          // No workspaces found, redirect to create-workspace page
          return redirect('/create-workspace');
        }
      }
    }

    // Check if activeWorkspace matches userAccount.activeWorkspace
    // Check if workspaces array matches userAccount.workspaces
    // If not, update the sessionUser.activeWorkspace and sessionUser.workspaces
    // and redirect to the home page
    if (
      (!sessionUser.activeWorkspace && userAccount.activeWorkspace) ||
      sessionUser.activeWorkspace?.toString() !==
        userAccount.activeWorkspace?.toString() ||
      sessionUser.workspaces.length !== userAccount.workspaces.length
    ) {
      // Update the sessionUser.activeWorkspace and sessionUser.workspaces
      const session = await getSession(request.headers.get('cookie'));
      const updatedUser = {
        ...sessionUser,
        activeWorkspace: userAccount.activeWorkspace,
        workspaces: userAccount.workspaces,
      };
      session.set('user', updatedUser);

      // Redirect to refresh the page with new active workspace
      return redirect('/', {
        headers: {
          'Set-Cookie': await commitSession(session),
        },
      });
    }

    if (userAccount) {
      // Add workspaces info to user session
      return {
        user: {
          ...sessionUser,
          workspaces: userAccount.workspaces,
          activeWorkspace: userAccount.activeWorkspace,
        },
      };
    }
  }

  return { user: sessionUser };
}

export function ErrorBoundary() {
  const error = useRouteError();

  return (
    <div className="min-h-screen flex flex-col justify-center items-center gap-4">
      <h1 className="text-xl font-bold">Oops, something went wrong!</h1>
      <p>Please try reloading and reach out to support if the error persists</p>
      <p>Error: {error ? error.message : 'Something went wrong'}</p>
    </div>
  );
}

export function Layout({ children }) {
  useEffect(() => {
    const moduleScript = document.createElement('script');
    moduleScript.type = 'module';
    moduleScript.src =
      'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js';
    document.body.appendChild(moduleScript);
  }, []);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Ubuntu+Mono&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          href="https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css"
        />
        <script src="https://js.stripe.com/v3/pricing-table.js"></script>
        <script src="https://kit.fontawesome.com/e8faad09b1.js"></script>
        <script
          src="https://app.bentonow.com/20957ebef171cecf9575f72cc925adb8.js"
          async
          defer
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`,
          }}
        />
        <script
          async
          src="https://affiliates.docsautomator.co/rw.js"
          data-rewardful="af394e"
        ></script>
      </head>
      <body
        style={{ backgroundImage: `url(${bgImage})` }}
        className="min-h-screen bg-fixed antialiased"
      >
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  return <Outlet />;
}

export default App;
